/*
 * label-maker/render.js — the on-screen half of the same geometry.
 *
 * This renderer consumes EXACTLY the item list that pdf.js draws — same
 * millimetres, same line breaks, same type sizes, decided once in layout.js.
 * Preview and print therefore cannot drift apart by construction, which is the
 * failure people describe as "на экране было нормально, а напечаталось не так".
 *
 * SVG rather than canvas: the sheet preview is mostly rectangles and text, it
 * has to stay sharp at any zoom, and it must be readable by the Playwright
 * tests that assert positions in millimetres.
 *
 * The single unit rule: an SVG user unit IS one millimetre. The viewBox is the
 * page in millimetres and the element is sized in CSS pixels, so scaling is the
 * browser's problem and no code here ever converts.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.LabelRender = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    var NS = 'http://www.w3.org/2000/svg';

    function el(name, attrs) {
        var n = document.createElementNS(NS, name);
        for (var k in attrs) {
            if (Object.prototype.hasOwnProperty.call(attrs, k) && attrs[k] != null) {
                n.setAttribute(k, String(attrs[k]));
            }
        }
        return n;
    }

    /* ── measurement ─────────────────────────────────────────────────────────
     * One shared canvas, metrics in millimetres. The font size passed to the
     * canvas is in px; we work at 100 px per em and divide, which keeps the
     * measurement well away from sub-pixel rounding at small type sizes.
     *
     * `document.fonts.check` matters: measuring against a fallback face while
     * the real one is still loading produces line breaks that change under the
     * user's hands a second later. Callers re-layout on `fontsReady`.
     */
    var _mc = null;
    function measurer(fontFamilyFor) {
        if (!_mc) _mc = document.createElement('canvas').getContext('2d');

        function setFont(style) {
            var fam = fontFamilyFor ? fontFamilyFor(style && style.font) : 'sans-serif';
            var weight = (style && style.bold) ? '700' : '400';
            _mc.font = weight + ' 100px ' + fam;
        }

        var fn = function (text, style) {
            setFont(style);
            var w = _mc.measureText(String(text == null ? '' : text)).width / 100;
            var size = (style && style.size) || 3;
            var ls = (style && style.letterSpacing) || 0;
            return w * size + ls * Math.max(0, String(text || '').length - 1);
        };

        /* Where the baseline sits below the top of the line box, as a fraction
         * of the em. THIS NUMBER MUST COME FROM ONE PLACE.
         *
         * It used to be a hard-coded 0.78 in the SVG renderer while the PDF
         * asked pdf-lib for the face's ascent — and Noto Sans has an ascent of
         * ~1.07 em, so every line printed ~0.29 em lower than the preview
         * showed. On a 14 mm type size that is 4 mm: the difference between
         * text centred in a label and text sitting on the die cut. Measured by
         * tests/playwright/label-maker-wysiwyg.mjs, which is what caught it.
         *
         * fontBoundingBoxAscent is the font's own ascender metric, the same
         * table pdf-lib reads, so taking it here and using it in BOTH backends
         * makes them agree by construction rather than by a tuned constant. */
        fn.ascentEm = function (style) {
            setFont(style);
            var m = _mc.measureText('Hg');
            var a = m.fontBoundingBoxAscent;
            if (!isFinite(a) || a <= 0) {
                // Safari before 16 has no fontBoundingBox*; actualBoundingBox
                // of a tall pair is a close stand-in, and 0.8 is the last
                // resort. Both backends still share whatever comes out.
                a = m.actualBoundingBoxAscent;
            }
            return (isFinite(a) && a > 0) ? a / 100 : 0.8;
        };

        return fn;
    }

    /* ── barcode options, defined ONCE ───────────────────────────────────────
     * The preview and the PDF each used to build their own bwip-js options, and
     * they disagreed about the two that matter: the PDF asked for a barcode
     * `wMm` × `hMm` millimetres, the preview asked for bwip's natural shape at
     * scale 3. Both then fitted the result into the same box with
     * preserveAspectRatio — so the preview showed a 54 mm code where the PDF
     * printed a 90 mm one. Same box, same value, different picture.
     *
     * `scale` is the only legitimate difference between the two callers: the
     * PDF wants enough pixels for 600 dpi, the screen does not. */
    function barcodeOptions(it, scale) {
        var w = it.box ? it.box.w : 20;
        var h = it.box ? it.box.h : 10;
        var gs1dm = it.symbology === 'gs1datamatrix';
        var opts = {
            /* GS1 DataMatrix is bwip's `datamatrix` + parsefnc with FNC1 first
               and <GS> separators — the text is built by LabelMatrix.gs1Text,
               the one place that knows the marking-code structure. */
            bcid: gs1dm ? 'datamatrix' : (it.symbology || 'qrcode'),
            text: gs1dm && window.LabelMatrix
                ? window.LabelMatrix.gs1Text(it.value)
                : String(it.value == null ? '' : it.value),
            includetext: !!it.showText,
            paddingwidth: 0, paddingheight: 0,
            backgroundcolor: 'FFFFFF',
            scale: Math.max(1, Math.round(scale || 3)),
            width: w,
        };
        if (gs1dm) { opts.parse = true; opts.parsefnc = true; }
        // A 2D symbol is square by definition; asking for a wide box would only
        // make bwip pad it, which wastes the label rather than filling it.
        if (it.symbology === 'qrcode' || it.symbology === 'datamatrix' || gs1dm || it.symbology === 'azteccode') {
            var side = Math.min(w, h);
            opts.width = side; opts.height = side;
        } else {
            opts.height = Math.max(4, h);
        }
        return opts;
    }

    /* The cache key must carry everything that changes the picture — including
     * the caption flag, or two elements differing only in it share one image. */
    function barcodeKey(it) {
        var r2 = function (v) { return Math.round(v * 100) / 100; };
        return (it.symbology || '') + '|' + it.value + '|'
            + r2(it.box ? it.box.w : 0) + 'x' + r2(it.box ? it.box.h : 0)
            + '|' + (it.showText ? 't' : 'n');
    }

    /* BOTH weights, always. Asking only for 400 — as this did until 2026-08-20 —
       resolves `document.fonts.ready` with the bold file still unrequested, and
       then `measurer` sizes bold text against the FALLBACK face. Measured on
       Caveat: 63.59 mm of fallback against 47.42 mm of the real face for the
       same string, a 34 % overestimate. The tool believes that, so it shrinks
       the type or declares "does not fit" for a line that fits with room to
       spare. It is a race — the bold file lands a moment later and the preview
       looks right — which is exactly why nobody saw it. */
    function fontsReady(families) {
        if (!document.fonts || !document.fonts.load) return Promise.resolve();
        var wants = [];
        (families || []).forEach(function (f) {
            wants.push('400 16px ' + f);
            wants.push('700 16px ' + f);
        });
        return Promise.all(wants.map(function (spec) {
            return document.fonts.load(spec).catch(function () { });
        })).then(function () { return document.fonts.ready; }).catch(function () { });
    }

    /* ── one label ───────────────────────────────────────────────────────── */
    function labelClip(id, label, fmt) {
        var cp = el('clipPath', { id: id });
        cp.appendChild(shapeNode(0, 0, label.w, label.h, fmt, {}));
        return cp;
    }

    function shapeNode(x, y, w, h, fmt, attrs) {
        var shape = fmt && fmt.shape;
        if (shape === 'circle' || shape === 'ellipse') {
            return el('ellipse', assign({ cx: x + w / 2, cy: y + h / 2, rx: w / 2, ry: h / 2 }, attrs));
        }
        return el('rect', assign({
            x: x, y: y, width: w, height: h,
            rx: (fmt && fmt.radius) || 0, ry: (fmt && fmt.radius) || 0,
        }, attrs));
    }

    /* Draw the item list into a <g> translated to the label's origin. */
    function drawItems(g, items, opts) {
        opts = opts || {};
        (items || []).forEach(function (it) {
            var b = it.box;
            if (it.kind === 'shape') {
                var node;
                if (it.shape === 'line') {
                    node = el('line', {
                        x1: b.x, y1: b.y + b.h / 2, x2: b.x + b.w, y2: b.y + b.h / 2,
                        stroke: it.stroke || it.fill || '#000', 'stroke-width': it.strokeWidth || 0.3,
                        'stroke-linecap': 'round',
                    });
                } else if (it.shape === 'circle' || it.shape === 'ellipse') {
                    node = el('ellipse', {
                        cx: b.x + b.w / 2, cy: b.y + b.h / 2, rx: b.w / 2, ry: b.h / 2,
                        fill: it.fill || 'none', stroke: it.stroke || 'none',
                        'stroke-width': it.strokeWidth || 0,
                    });
                } else {
                    node = el('rect', {
                        x: b.x, y: b.y, width: b.w, height: b.h,
                        rx: it.radius || 0, ry: it.radius || 0,
                        fill: it.fill || 'none', stroke: it.stroke || 'none',
                        'stroke-width': it.strokeWidth || 0,
                    });
                }
                g.appendChild(node);
                return;
            }

            /* A code sized in printer dots (layout marked it `matrix`): drawn
               from its MODULES at the size and dot-snapped place LabelMatrix
               decides — the PDF asks the same two functions with the same
               origin, so preview and print agree to the dot. The symbol comes
               from the caller (`opts.matrixFor`), which owns bwip-js and the
               cache; until it is there, the same dashed frame as any pending
               code. */
            if (it.kind === 'barcode' && it.matrix) {
                var LM = window.LabelMatrix;
                var enc = (LM && opts.matrixFor) ? opts.matrixFor(it) : null;
                if (!enc) {
                    g.appendChild(el('rect', {
                        x: b.x, y: b.y, width: b.w, height: b.h,
                        fill: 'none', stroke: '#c8c8c8', 'stroke-width': 0.2,
                        'stroke-dasharray': '1 1',
                    }));
                    return;
                }
                var geom = LM.geometry(it, enc);
                var pl = LM.place(it, geom, opts.origin || { x: 0, y: 0 });
                var path = el('path', {
                    d: LM.svgPath(enc, pl), fill: it.color || '#000',
                    'shape-rendering': 'crispEdges', 'data-lm-matrix': enc.size,
                });
                g.appendChild(path);
                return;
            }

            if (it.kind === 'image' || it.kind === 'barcode') {
                var href = it.kind === 'image' ? it.src : (opts.barcodeUrl ? opts.barcodeUrl(it) : null);
                if (!href) {
                    // A barcode that has not rendered yet is shown as an empty
                    // frame rather than nothing, so the layout does not jump.
                    g.appendChild(el('rect', {
                        x: b.x, y: b.y, width: b.w, height: b.h,
                        fill: 'none', stroke: '#c8c8c8', 'stroke-width': 0.2,
                        'stroke-dasharray': '1 1',
                    }));
                    return;
                }
                var img = el('image', {
                    x: b.x, y: b.y, width: b.w, height: b.h,
                    preserveAspectRatio: it.fit === 'cover' ? 'xMidYMid slice' : 'xMidYMid meet',
                });
                img.setAttributeNS('http://www.w3.org/1999/xlink', 'href', href);
                img.setAttribute('href', href);
                g.appendChild(img);
                return;
            }

            if (it.kind === 'text') {
                var block = it.lines.length * it.lineHeight;
                var top = b.y;
                if (it.valign === 'middle') top = b.y + (b.h - block) / 2;
                else if (it.valign === 'bottom') top = b.y + b.h - block;

                var anchor = it.align === 'center' ? 'middle' : (it.align === 'right' ? 'end' : 'start');
                var tx = it.align === 'center' ? b.x + b.w / 2 : (it.align === 'right' ? b.x + b.w : b.x);

                for (var i = 0; i < it.lines.length; i++) {
                    if (it.lines[i] === '') continue;
                    var t = el('text', {
                        x: tx,
                        // The baseline offset was measured once, in layout,
                        // from the face's own ascent metric — the PDF uses the
                        // very same number.
                        y: top + i * it.lineHeight + it.size * (it.ascentEm || 0.8),
                        'text-anchor': anchor,
                        'font-family': opts.fontFamilyFor ? opts.fontFamilyFor(it.font) : 'sans-serif',
                        'font-size': it.size,
                        'font-weight': it.bold ? 700 : 400,
                        'letter-spacing': it.letterSpacing || null,
                        fill: it.color || '#000',
                        'xml:space': 'preserve',
                        /* `calt` OFF, and only `calt`.

                           pdf-lib does shape: it runs fontkit's `layout()`, so
                           GSUB and GPOS DO reach the page. But the two shapers
                           do not agree feature for feature, and guessing which
                           is which is how this went wrong twice. Measured, one
                           feature at a time, screen raster against PDF raster:

                             switched off        lobster        caveat
                             ─────────────────────────────────────────
                             nothing             1.000          1.024  ✗
                             liga + clig         0.955  ✗       1.024  ✗
                             ligatures: none     0.955  ✗       1.000
                             calt                1.000          1.000  ✓

                           So fontkit applies `liga`/`clig` exactly as the
                           browser does — switching those off in the preview
                           BREAKS a face that was already correct — and it does
                           not apply `calt`, which is what made Caveat print
                           0.64 mm wider than it previewed. One feature off,
                           every face agreeing, nothing tuned per face. */
                        style: 'font-feature-settings:"calt" 0',
                    });
                    t.textContent = it.lines[i];
                    g.appendChild(t);
                }
            }
        });
    }

    /* ── the sheet ───────────────────────────────────────────────────────────
     * `cells[i]` is either null (deliberately blank — a used-up label) or the
     * layout result for that position. */
    function renderSheet(host, fmt, cells, opts) {
        opts = opts || {};
        var S = window.LabelSheets;
        var page = S.page(fmt.page);
        var perSheet = S.perSheet(fmt);
        var pageIndex = opts.page || 0;

        host.textContent = '';
        var svg = el('svg', {
            viewBox: '0 0 ' + page.w + ' ' + page.h,
            width: '100%',
            preserveAspectRatio: 'xMidYMid meet',
            role: 'img',
            'aria-label': opts.ariaLabel || '',
        });
        svg.style.display = 'block';

        var defs = el('defs', {});
        defs.appendChild(labelClip('lm-clip', { w: fmt.w, h: fmt.h }, fmt));
        svg.appendChild(defs);

        svg.appendChild(el('rect', {
            x: 0, y: 0, width: page.w, height: page.h,
            fill: opts.paper || '#ffffff', stroke: 'rgba(128,128,128,.55)', 'stroke-width': 0.3,
        }));

        // The band a printer cannot mark. Shown because a full-bleed sheet that
        // will lose its outer 5 mm should look wrong on screen too.
        if (opts.showDeadZone) {
            var d = S.DEAD_ZONE_MM;
            svg.appendChild(el('rect', {
                x: d / 2, y: d / 2, width: page.w - d, height: page.h - d,
                fill: 'none', stroke: 'rgba(230,140,60,.5)', 'stroke-width': d,
                'stroke-dasharray': null, opacity: 0.16,
            }));
        }

        for (var i = 0; i < perSheet; i++) {
            var cell = S.cellAt(fmt, i, { order: opts.order, correction: opts.correction });
            var idx = pageIndex * perSheet + i;
            var content = cells ? cells[idx] : null;
            var used = (content === null || content === undefined);

            var outline = shapeNode(cell.x, cell.y, cell.w, cell.h, fmt, {
                fill: used ? 'rgba(128,128,128,.09)' : (opts.labelFill || 'rgba(0,0,0,0)'),
                stroke: used ? 'rgba(128,128,128,.35)' : 'rgba(90,110,150,.55)',
                'stroke-width': 0.22,
                'stroke-dasharray': used ? '1.4 1.2' : null,
                'data-cell': i,
            });
            outline.classList.add('lm-cell');
            if (used) outline.classList.add('is-used');
            svg.appendChild(outline);

            if (content && content.items) {
                // Same scale the PDF applies to the content (see pdf.js/drawCell):
                // the preview must show the corrected size, not just the
                // corrected position, or it stops predicting the paper.
                var sc = (opts.correction && opts.correction.scale) || 1;
                var g = el('g', {
                    transform: 'translate(' + cell.x + ' ' + cell.y + ')'
                        + (sc !== 1 ? ' scale(' + sc + ')' : ''),
                });
                if (fmt.shape === 'circle' || fmt.shape === 'ellipse' || fmt.radius > 0) {
                    g.setAttribute('clip-path', 'url(#lm-clip)');
                }
                // The cell corner on the page: a dot-sized code snaps to the
                // printer's grid from the PAGE edge, as it does in the PDF.
                drawItems(g, content.items, assign({}, opts, { origin: { x: cell.x, y: cell.y } }));
                svg.appendChild(g);
            }

            if (opts.showNumbers) {
                var n = el('text', {
                    x: cell.x + 0.9, y: cell.y + 2.6,
                    'font-size': 2.1, fill: 'rgba(120,130,150,.75)',
                    'font-family': 'ui-monospace, monospace',
                });
                n.textContent = String(idx + 1);
                svg.appendChild(n);
            }
        }

        // The printed scale check, drawn where the PDF will put it, so the
        // preview does not hide a mark that will appear on paper.
        if (opts.ruler) {
            var slot = S.rulerSlot(fmt);
            if (slot) {
                var rg = el('g', { opacity: 0.75 });
                rg.appendChild(el('line', {
                    x1: slot.x, y1: slot.y + 4, x2: slot.x + slot.len, y2: slot.y + 4,
                    stroke: '#777', 'stroke-width': 0.3,
                }));
                for (var mm = 0; mm <= slot.len; mm += 10) {
                    rg.appendChild(el('line', {
                        x1: slot.x + mm, y1: slot.y + 4, x2: slot.x + mm,
                        y2: slot.y + (mm % 50 === 0 ? 0.8 : 2),
                        stroke: '#777', 'stroke-width': 0.3,
                    }));
                }
                svg.appendChild(rg);
            }
        }

        host.appendChild(svg);
        return svg;
    }

    /* ── one label, big ──────────────────────────────────────────────────── */
    function renderLabel(host, fmt, content, opts) {
        opts = opts || {};
        host.textContent = '';
        var svg = el('svg', {
            viewBox: '0 0 ' + fmt.w + ' ' + fmt.h,
            width: '100%',
            preserveAspectRatio: 'xMidYMid meet',
        });
        svg.style.display = 'block';

        var defs = el('defs', {});
        defs.appendChild(labelClip('lm-clip-one', { w: fmt.w, h: fmt.h }, fmt));
        svg.appendChild(defs);

        svg.appendChild(shapeNode(0.15, 0.15, fmt.w - 0.3, fmt.h - 0.3, fmt, {
            fill: opts.paper || '#ffffff',
            stroke: 'rgba(120,130,150,.75)', 'stroke-width': 0.3,
        }));

        if (opts.showSafe) {
            var s = opts.safe == null ? 1.5 : opts.safe;
            svg.appendChild(shapeNode(s, s, Math.max(0, fmt.w - s * 2), Math.max(0, fmt.h - s * 2), fmt, {
                fill: 'none', stroke: 'rgba(60,160,110,.6)', 'stroke-width': 0.18,
                'stroke-dasharray': '1 0.8',
            }));
        }

        if (content && content.items) {
            var g = el('g', {});
            if (fmt.shape === 'circle' || fmt.shape === 'ellipse' || fmt.radius > 0) {
                g.setAttribute('clip-path', 'url(#lm-clip-one)');
            }
            drawItems(g, content.items, opts);
            svg.appendChild(g);

            /* Selection frame + resize grip for the element being edited. Drawn
             * OUTSIDE the clip group so a handle on a circular label stays
             * grabbable at the rim. */
            if (opts.selected != null && content.items[opts.selected]) {
                var b = content.items[opts.selected].box;
                var pad = 0.25;
                svg.appendChild(el('rect', {
                    x: b.x - pad, y: b.y - pad, width: b.w + pad * 2, height: b.h + pad * 2,
                    fill: 'none', stroke: 'rgba(70,130,240,.95)', 'stroke-width': 0.28,
                    'stroke-dasharray': '1.1 0.7', 'pointer-events': 'none',
                    'data-lm-sel': '1',
                }));
                var grip = Math.max(1.6, Math.min(fmt.w, fmt.h) * 0.075);
                svg.appendChild(el('rect', {
                    x: b.x + b.w - grip / 2, y: b.y + b.h - grip / 2,
                    width: grip, height: grip, rx: grip * 0.25,
                    fill: 'rgba(70,130,240,.95)', stroke: '#fff', 'stroke-width': 0.2,
                    'data-lm-grip': '1', style: 'cursor:nwse-resize',
                }));
            }
        }

        host.appendChild(svg);
        return svg;
    }

    /* Client coordinates → label millimetres. The SVG's own CTM is the only
     * honest converter: it already accounts for the viewBox, the element's CSS
     * size, page zoom and any transform an ancestor applies. */
    function svgPoint(svg, clientX, clientY) {
        var pt = svg.createSVGPoint();
        pt.x = clientX; pt.y = clientY;
        var m = svg.getScreenCTM();
        if (!m) return { x: 0, y: 0 };
        var p = pt.matrixTransform(m.inverse());
        return { x: p.x, y: p.y };
    }

    /* Which drawn item sits under a point, topmost first. */
    function hitTest(items, x, y) {
        for (var i = (items || []).length - 1; i >= 0; i--) {
            var b = items[i].box;
            if (x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h) return i;
        }
        return -1;
    }

    function assign(t) {
        for (var i = 1; i < arguments.length; i++) {
            var s = arguments[i];
            if (!s) continue;
            for (var k in s) if (Object.prototype.hasOwnProperty.call(s, k)) t[k] = s[k];
        }
        return t;
    }

    return {
        measurer: measurer,
        fontsReady: fontsReady,
        renderSheet: renderSheet,
        renderLabel: renderLabel,
        drawItems: drawItems,
        svgPoint: svgPoint,
        hitTest: hitTest,
        barcodeOptions: barcodeOptions,
        barcodeKey: barcodeKey,
    };
});
